import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import KeyboardArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardArrowLeftOutlined';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import { Box, Divider, Fab, Grid, Stack, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { Element } from 'react-scroll';
import { ApplicantTypeEnum, CPDLicenseTypeEnum, ICourse, ICustomGlance } from 'src/interfaces';

import { CourseDetailStyle, Container, ImageBox, VideoContainer } from './style';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import CertificateCriteria from '@components/molecules/certificateCriteria';
import CollapseCourseDescription from '@components/molecules/collapseCourseDescription';
import CourseDetailHaveCourseTag from '@components/molecules/courseDetailHaveCourseTag';
import CurriculumCourseDetail from '@components/molecules/curriculum/curriculumCourseDetail';
import InstructorCourseDetail from '@components/molecules/instructorCourseDetail/cpd';
import VideoJs from '@components/molecules/videojs';
import { CardDescription } from '@components/templates/coursesCpdDetail/style';

type Props = {
  course?: ICourse;
  onNext: Function;
  onPrev: Function;
  totalCourse: number;
  currentIndex: number;
  bundleName?: string;
};

type TextIcon = {
  icon?: JSX.Element;
  title?: string;
  subTitle?: string;
};

const CourseDetail = ({ course, onNext, onPrev, totalCourse, currentIndex, bundleName }: Props) => {
  const { t } = useTranslation('common');
  const [currentPreviewVideo, setCurrentPreviewVideo] = useState<string>(course?.videoPreviewURL || '');
  const videoRef = useRef<any>();
  const scrollRef = useRef<any>();
  const isFirstItem = () => currentIndex == 0;

  const isLastItem = () => currentIndex + 1 == totalCourse;

  const onChangeVideo = (videoURL: string) => {
    if (videoURL) {
      setCurrentPreviewVideo(videoURL);
      scrollRef.current?.scrollIntoView();
    }
  };

  useEffect(() => {
    if (course?.videoPreviewURL) {
      setCurrentPreviewVideo(course.videoPreviewURL);
    }
  }, [course?.videoPreviewURL]);

  const renderCustomGlance = () =>
    course?.custom_glances.map(({ name = '' }: ICustomGlance, index) => (
      <Stack className="custom-glance" direction="row" key={index}>
        <FontAwesomeIcon icon="fa-light fa-check" />
        <span>{name}</span>
      </Stack>
    ));

  const glancesVideo = () => {
    const hours = Math.floor((course?.default_glances?.total_hours || 0) / 3600);
    const minute = Math.floor(((course?.default_glances?.total_hours || 0) / 60) % 60);
    if (hours < 1) {
      return {
        icon: <FontAwesomeIcon icon="fa-light fa-circle-play" />,
        title: t('cpd_course.default_glance.all_video', { total: course?.default_glances?.total_videos }),
        subTitle: t('cpd_course.default_glance.all_video.time.only_minute', {
          minute,
        }),
      };
    }

    return {
      icon: <FontAwesomeIcon icon="fa-light fa-circle-play" />,
      title: t('cpd_course.default_glance.all_video', { total: course?.default_glances?.total_videos }),
      subTitle: t('cpd_course.default_glance.all_video.time', {
        hours,
        minute,
      }),
    };
  };

  const glancesAvailableDaysToLearnRegular = (countDownDay: number) => {
    if (countDownDay > 0) {
      return {
        icon: <FontAwesomeIcon icon="fa-light fa-infinity" />,
        title: t('home.course_card_can_learn_have_countdown', { days: countDownDay }),
      };
    }

    return {
      icon: <FontAwesomeIcon icon="fa-light fa-infinity" />,
      title: t('home.course_card_can_learn'),
    };
  };

  const glancesAvailableDaysToLearnCPD = () => ({
    icon: <FontAwesomeIcon icon="fa-light fa-hourglass-start" />,
    title: t('cpd_course.default_glance.available_days_to_learn', {
      days: (course?.default_glances?.available_days_to_learn || 0) + 1,
    }),
    subTitle: `${t('cpd_course.default_glance.available_days_to_learn.description')}\n${t(
      'cpd_course.default_glance.available_days_to_learn.remark',
    )}`,
  });

  const glancesCertificateRegular = () => ({
    icon: <Image layout="fixed" src="/static/icons/glance-certificate.svg" width="30px" height="20px" alt="cert" />,
    title: t('home.course_card_certificate'),
  });

  const glancesCertificateCPD = () => {
    const licenseTypes = () => {
      if (course?.licenseRenewal === 4) return t('cpd_course.license_types.oic.life_and_non_life');
      if (course?.licenseTypes?.find((f) => f === CPDLicenseTypeEnum.LIFE))
        return t('cpd_course.license_types.oic.life');
      if (course?.licenseTypes?.find((f) => f === CPDLicenseTypeEnum.NON_LIFE))
        return t('cpd_course.license_types.oic.non_life');
      if (course?.licenseTypes?.find((f) => f === CPDLicenseTypeEnum.INVESTMENT))
        return t('cpd_course.license_types.tsi.investment');
      return '';
    };
    const applicantType = () => {
      if (course?.applicantType === ApplicantTypeEnum.BROKER) {
        return t('cpd_course.applicant_type.broker');
      }
      if (course?.applicantType === ApplicantTypeEnum.AGENT) {
        return t('cpd_course.applicant_type.agent');
      }
    };

    const glanceSubtitleText = () => {
      if (course?.licenseTypes?.find((licenseType) => licenseType === CPDLicenseTypeEnum.INVESTMENT))
        return t('cpd_course.default_glance.certificate.description.tsi');
      return t('cpd_course.default_glance.certificate.description.oic');
    };

    return {
      icon: <Image layout="fixed" src="/static/icons/glance-certificate.svg" width="30px" height="20px" alt="cert" />,
      title: t('cpd_course.default_glance.certificate', {
        applicant_type: applicantType(),
        license_types: licenseTypes(),
        collect_hours: course?.default_glances?.collect_hours,
      }),
      subTitle: glanceSubtitleText(),
    };
  };

  const glancesFileDownload = () => ({
    icon: <FontAwesomeIcon icon="fa-light fa-file-arrow-down" />,
    title: t('cpd_course.default_glance.file_download'),
    subTitle: t('cpd_course.default_glance.file_download.description', {
      total: course?.default_glances?.total_downloaded_files,
    }),
  });

  const glancesExam = () => ({
    icon: <FontAwesomeIcon icon="fa-light fa-circle-question" />,
    title: t('cpd_course.default_glance.exam'),
    subTitle: t('cpd_course.default_glance.exam.description', { total: course?.default_glances?.total_quizzes }),
  });

  const glancesArticle = () => ({
    icon: <FontAwesomeIcon icon="fa-light fa-file-lines" />,
    title: t('cpd_course.default_glance.article'),
    subTitle: t('cpd_course.default_glance.article.description', {
      total: course?.default_glances?.total_articles,
    }),
  });

  const renderDefaultGlance = (isCpd: boolean) => {
    const list: TextIcon[] = [];
    if (course?.default_glances?.total_hours) list.push(glancesVideo());
    if (isCpd) {
      if (course?.default_glances?.available_days_to_learn) list.push(glancesAvailableDaysToLearnCPD());
      if (course?.default_glances?.collect_hours) list.push(glancesCertificateCPD());
    } else {
      list.push(glancesAvailableDaysToLearnRegular(course?.countdown_day!));
      if (course?.have_certificate) list.push(glancesCertificateRegular());
    }
    if (course?.default_glances?.total_downloaded_files) list.push(glancesFileDownload());
    if (course?.default_glances?.total_quizzes) list.push(glancesExam());
    if (course?.default_glances?.total_articles) list.push(glancesArticle());

    return (
      <Grid container spacing={1} sx={{}}>
        {list.map(({ icon, title = '', subTitle = '' }: TextIcon, index) => (
          <Grid item xs={12} md={6} lg={12} key={index}>
            <Box className="default-glance">
              <Box sx={{ ml: '8px', display: 'flex', textAlign: 'center', justifyContent: 'center' }}>{icon}</Box>
              <Box sx={{ marginLeft: '32px' }}>
                <Typography variant="h4" sx={{ lineHeight: '20px', fontSize: '20px', whiteSpace: 'pre-wrap' }}>
                  {title}
                </Typography>
                <div className="subtitle">{subTitle}</div>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <CourseDetailStyle>
      {course && (
        <Stack flexDirection="column" sx={{ minHeight: '100vh' }} ref={scrollRef}>
          <Container>
            <Box className="fix-top" sx={{ '& > :not(style)': { m: 1 } }}>
              <Fab className="pagination-label-button" aria-label="prev" disabled={isFirstItem()}>
                <KeyboardArrowLeftOutlinedIcon onClick={() => onPrev()} />
              </Fab>
              <Fab className="pagination-label" variant="extended">
                <span>
                  {t('course_detail.pagination', {
                    current_index: currentIndex + 1,
                    total_course: totalCourse,
                  })}
                </span>
              </Fab>
              <Fab className="pagination-label-button" aria-label="next" disabled={isLastItem()}>
                <KeyboardArrowRightOutlinedIcon onClick={() => onNext()} />
              </Fab>
            </Box>

            {course.videoPreviewURL ? (
              <VideoContainer>
                <VideoJs ref={videoRef} videoUrl={currentPreviewVideo} fluid />
              </VideoContainer>
            ) : (
              <ImageBox>
                <Image
                  src={course.highlight_image_original}
                  alt="course.highlight_original_image"
                  layout="fill"
                  priority
                />
              </ImageBox>
            )}
            <Box className="container" style={{ marginTop: '30px' }}>
              <div>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12} lg={8} order={{ lg: 1, xs: 1 }}>
                    <Element name="coursesDetail" />
                    <div className="course-title">
                      {course.isEnrolled && (
                        <div style={{ marginBottom: '15px' }}>
                          <CourseDetailHaveCourseTag isCPD={course.product.isCpd} />
                        </div>
                      )}
                      <Typography color="rgba(0, 0, 0, 0.60)" variant="subtitle1" lineHeight="1.0">
                        {course.product.isCpd ? (
                          <>{t('course_detail.course_cpd_title')} </>
                        ) : (
                          <>{t('course_detail.course_regular_title')} </>
                        )}
                        {t('course_detail.bundle_name', {
                          bundle_name: bundleName,
                        })}
                      </Typography>
                      <Typography color="black" variant="h3" fontWeight="900" style={{ marginTop: '15px' }}>
                        {currentIndex + 1}. {course.name}
                      </Typography>
                    </div>
                  </Grid>
                  <Grid item xs={12} md={12} lg={4} order={{ lg: 2, xs: 2 }}>
                    <Element name="defaultGlance" />
                    <div className="course-description-title-mobile">
                      <Typography color="black" variant="h4" fontWeight="500">
                        {course.product.isCpd ? (
                          <span>{t('cpd_course.course_description.title')}</span>
                        ) : (
                          <span>{t('course_detail.title_detail')}</span>
                        )}
                      </Typography>
                      <Box mt={1} mb={4}>
                        <Divider />
                      </Box>
                    </div>
                    <div>{renderDefaultGlance(course.product.isCpd || false)}</div>
                  </Grid>
                  <Grid item xs={12} md={12} lg={8} order={{ lg: 1, xs: 3 }}>
                    <div>
                      <div className="course-description-title-desktop">
                        <Typography color="black" variant="h4" fontWeight="500" className="course-description-title">
                          {course.product.isCpd ? (
                            <span>{t('cpd_course.course_description.title')}</span>
                          ) : (
                            <span>{t('course_detail.title_detail')}</span>
                          )}
                        </Typography>
                        <Box mt={1} mb={2}>
                          <Divider />
                        </Box>
                      </div>
                      {course.custom_glances.length > 0 && (
                        <CardDescription>
                          <Typography color="primary.main" variant="h6_deprecated">
                            {t('cpd_course.course_description.result')}
                          </Typography>
                          {renderCustomGlance()}
                        </CardDescription>
                      )}
                      <Box paddingY="10px">
                        <CollapseCourseDescription
                          seoDescription={course?.seoDescription}
                          description={course?.description!}
                        />
                      </Box>
                      {course.have_certificate && !course.product.isCpd && (
                        <Box>
                          <Typography color="black" variant="h4" fontWeight="500" className="course-description-title">
                            {t('course_detail.certificate_criteria.title')}
                          </Typography>
                          <Box mt={1} mb={2}>
                            <Divider />
                          </Box>
                          <CertificateCriteria
                            courseItems={course.course_items}
                            criteriaLearnPercent={course.product.course?.criteria_learn_percent}
                          />
                        </Box>
                      )}
                    </div>
                  </Grid>
                  <Grid item xs={12} md={12} lg={8} order={{ lg: 4, xs: 4 }}>
                    <CurriculumCourseDetail
                      parts={course.parts!.filter((part) => part.course_items.length)}
                      onChangeVideo={onChangeVideo}
                      isDisplay
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={4} order={{ lg: 4, xs: 4 }}>
                    <Element name="instructor" />
                    <Grid
                      container
                      direction="column"
                      justifyContent="center"
                      alignItems="flex-start"
                      sx={{ marginBottom: 1, height: '40px' }}
                    >
                      <Typography color="black" variant="h4" fontWeight="500">
                        {t('cpd_course.tab_menu.instructor')}
                      </Typography>
                    </Grid>
                    <InstructorCourseDetail course={course} />
                  </Grid>
                </Grid>
                {/* TODO: uncomment after resolved retail price distribution in cpd free coues in cpd bundle issue  */}
                {/* <div style={{ marginTop: '50px' }}>
                  <NextLink href={`${config.APP_DOMAIN}/courses/${course.id_name}`} target="_blank">
                    <Typography align="center" color="#00532A" variant="body1" fontWeight="500">
                      {t('course_detail.link_to_course_detail', {
                        course_name: course.name,
                      })}
                      <OpenInNewIcon style={{ verticalAlign: 'bottom', marginLeft: '15px' }} />
                    </Typography>
                  </NextLink>
                </div> */}
              </div>
            </Box>
          </Container>
        </Stack>
      )}
    </CourseDetailStyle>
  );
};

export default CourseDetail;
